import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MenuItem, TreeDragDropService, TreeNode } from 'primeng/api';

import { EditData } from '../editable-list/editable-list.component';
import { buildSourceTree, isInSourceTier, joinSourcePath, splitSourcePath } from '../source-tiers';

/**
 * Tree analogue of EditableListComponent for tiered options stored as flat
 * ' > '-separated path strings (e.g. 'Realtor > Remax > Betty White').
 *
 * Same contract as the editable list: takes the flat `items` array and emits
 * the full updated array via `itemsEdited`; the parent persists it and closes
 * the open dialog. Rows render as a tree with per-node actions
 * (add sub-option / rename / delete) instead of a flat listbox.
 */
@Component({
  selector: 'app-editable-tree',
  templateUrl: './editable-tree.component.html',
  styleUrls: ['./editable-tree.component.scss'],
  // PrimeNG's Tree injects this @Optional() to coordinate drag/drop; without it
  // enabling draggableNodes crashes on `dragStart$`/`startDrag`.
  providers: [TreeDragDropService]
})
export class EditableTreeComponent implements OnChanges {

  @Input() items: string[];

  @Input() editable: boolean;

  @Input() editWarning?: string;

  @Input() deleteWarning?: string;

  @Input() height = '40vh';

  @Output() itemsEdited = new EventEmitter<EditData>();

  nodes: TreeNode[] = [];

  actions: MenuItem[] = [];

  addDialogVisible = false;

  editDialogVisible = false;

  deleteDialogVisible = false;

  /** Path of the node the add dialog adds under; undefined = add a top-level item. */
  addParentPath?: string;

  newItemName = '';

  /** Set when an add would collide with an existing item; shown in the dialog. */
  addError = '';

  /** Node targeted by the rename/delete dialogs. */
  activeNode?: TreeNode;

  renameValue = '';

  /** Set when a rename would collide with an existing item; shown in the dialog. */
  renameError = '';

  /** Full paths of every node in the active node's subtree (itself included). */
  activeSubtreePaths: string[] = [];

  private expandedPaths = new Set<string>();

  ngOnChanges(_changes: SimpleChanges): void {
    this.nodes = buildSourceTree(this.items || []);
    this.applyExpansion(this.nodes);
  }

  onNodeExpand(event: { node: TreeNode }) {
    this.expandedPaths.add(event.node.key || '');
  }

  onNodeCollapse(event: { node: TreeNode }) {
    this.expandedPaths.delete(event.node.key || '');
  }

  /**
   * PrimeNG has already moved the dragged node within `this.nodes` (we leave
   * `validateDrop` off, so the mutation happens before this fires). Reorder and
   * reparenting both surface here as a changed tree shape. Reconstruct the flat
   * array from the new structure: a node's path is its ancestors' labels joined,
   * so paths follow the node wherever it lands, and DFS order keeps subtrees
   * contiguous to match how `addItem` groups siblings.
   */
  onNodeDrop() {
    // Which paths were stored explicitly before the move. Every leaf qualifies
    // (each came from an item); standalone intermediates may too. Keyed by the
    // pre-move path, which is what the moved nodes still carry as their `key`.
    const explicitPaths = new Set(
      (this.items || []).map((item) => joinSourcePath(splitSourcePath(item)))
    );

    const items: string[] = [];
    const seen = new Set<string>();
    // Old path -> new path, to carry expansion state across a reparent.
    const pathRemap = new Map<string, string>();

    const walk = (nodes: TreeNode[], parentTiers: string[]) => {
      for (const node of nodes) {
        const tiers = [ ...parentTiers, node.label || '' ];
        const newPath = joinSourcePath(tiers);
        if (node.key) { pathRemap.set(node.key, newPath); }

        // Emit leaves (incl. a parent left childless by the move, mirroring
        // deleteItem) and any explicit intermediate; dedupe so a reparent
        // collision merges rather than duplicating a path.
        const isLeaf = !node.children?.length;
        if ((isLeaf || explicitPaths.has(node.key || '')) && !seen.has(newPath)) {
          seen.add(newPath);
          items.push(newPath);
        }

        if (node.children?.length) {
          walk(node.children, tiers);
        }
      }
    };
    walk(this.nodes, []);

    this.expandedPaths = new Set(
      Array.from(this.expandedPaths).map((path) => pathRemap.get(path) ?? path)
    );

    this.itemsEdited.emit({
      items,
      editType: 'edit',
    });
  }

  setActions(node: TreeNode) {
    this.actions = [
      {
        label: 'Add sub-option',
        icon: 'pi pi-plus',
        command: () => this.openAddDialog(node.key),
      },
      {
        label: 'Rename',
        icon: 'pi pi-pencil',
        command: () => this.openEditDialogToNode(node),
      },
      {
        label: 'Delete',
        icon: 'pi pi-trash',
        command: () => this.openDeleteDialogToNode(node),
      },
    ];
  }

  openAddDialog(parentPath?: string) {
    this.addParentPath = parentPath;
    this.newItemName = '';
    this.addError = '';
    this.addDialogVisible = true;
  }

  closeAddDialog() {
    this.addDialogVisible = false;
    this.addParentPath = undefined;
    this.newItemName = '';
    this.addError = '';
  }

  openEditDialogToNode(node: TreeNode) {
    this.activeNode = node;
    this.renameValue = node.label || '';
    this.renameError = '';
    this.activeSubtreePaths = this.collectSubtreePaths(node);
    this.editDialogVisible = true;
  }

  closeEditDialog() {
    this.editDialogVisible = false;
    this.activeNode = undefined;
    this.renameError = '';
  }

  openDeleteDialogToNode(node: TreeNode) {
    this.activeNode = node;
    this.activeSubtreePaths = this.collectSubtreePaths(node);
    this.deleteDialogVisible = true;
  }

  closeDeleteDialog() {
    this.deleteDialogVisible = false;
    this.activeNode = undefined;
  }

  addItem() {
    // splitSourcePath normalizes the name; a name containing ' > ' adds a
    // whole sub-path at once, mirroring how the config stores paths.
    const nameTiers = splitSourcePath(this.newItemName);
    if (!nameTiers.length) { return; }

    const currentItems = this.items || [];
    const parentTiers = splitSourcePath(this.addParentPath || '');
    const newPath = joinSourcePath([ ...parentTiers, ...nameTiers ]);

    // Reject an add that would duplicate an existing item; the flat array must
    // not carry the same path twice (the tree merges them silently otherwise).
    const collides = currentItems.some((item) =>
      joinSourcePath(splitSourcePath(item)) === newPath
    );
    if (collides) {
      this.addError = `"${newPath}" already exists.`;
      return;
    }

    let items: string[];

    if (!parentTiers.length) {
      items = [ newPath, ...currentItems ];
    } else {
      // Insert right after the parent's subtree so siblings stay grouped.
      const insertAt = this.lastIndexInTier(currentItems, this.addParentPath || '') + 1;
      items = [ ...currentItems ];
      items.splice(insertAt, 0, newPath);

      // Expand the ancestor chain so the new item is visible after the rebuild.
      for (let depth = 1; depth <= parentTiers.length; depth++) {
        this.expandedPaths.add(joinSourcePath(parentTiers.slice(0, depth)));
      }
    }

    this.itemsEdited.emit({
      items,
      editType: 'add',
    });
  }

  renameItem() {
    const newSegmentTiers = splitSourcePath(this.renameValue);
    if (!this.activeNode || !newSegmentTiers.length) { return; }

    const oldPath = this.activeNode.key || '';
    const oldTiers = splitSourcePath(oldPath);
    const newTiers = [ ...oldTiers.slice(0, -1), ...newSegmentTiers ];

    const renamePath = (path: string) => {
      const descendantTiers = splitSourcePath(path).slice(oldTiers.length);
      return joinSourcePath([ ...newTiers, ...descendantTiers ]);
    };

    // Reject a rename that would collide with an existing item outside the
    // renamed subtree; otherwise the flat array would carry a duplicate path
    // (the tree merges them silently, so the corruption is invisible here).
    const newPath = joinSourcePath(newTiers);
    const collides = (this.items || []).some((item) =>
      !isInSourceTier(item, oldPath)
      && joinSourcePath(splitSourcePath(item)) === newPath
    );
    if (collides) {
      this.renameError = `"${newPath}" already exists.`;
      return;
    }

    const items = (this.items || []).map((item) =>
      isInSourceTier(item, oldPath) ? renamePath(item) : item
    );

    // Carry expansion state over to the renamed paths.
    this.expandedPaths = new Set(
      Array.from(this.expandedPaths).map((path) =>
        isInSourceTier(path, oldPath) ? renamePath(path) : path
      )
    );

    this.itemsEdited.emit({
      items,
      editType: 'edit',
    });
  }

  deleteItem() {
    if (!this.activeNode) { return; }

    const path = this.activeNode.key || '';
    const currentItems = this.items || [];
    const parentPath = joinSourcePath(splitSourcePath(path).slice(0, -1));

    const items = currentItems.filter((item) => !isInSourceTier(item, path));

    // Requirement: Preserve parent nodes when deleting their last explicit child.
    // Tiered paths are stored flat; "A > B > C" implies "A > B" exists in the tree.
    // If "A > B > C" was the only item under "A > B", deleting it would remove "A > B"
    // from the UI, making it impossible to add new children under "A > B". We re-insert
    // the parent explicitly so users can still interact with it.
    if (parentPath && !items.some((item) => isInSourceTier(item, parentPath))) {
      const removedAt = currentItems.findIndex((item) => isInSourceTier(item, path));
      const insertAt = currentItems
        .slice(0, removedAt)
        .filter((item) => !isInSourceTier(item, path))
        .length;
      items.splice(insertAt, 0, parentPath);
    }

    this.itemsEdited.emit({
      items,
      editType: 'delete',
    });
  }

  private applyExpansion(nodes: TreeNode[]) {
    for (const node of nodes) {
      node.expanded = this.expandedPaths.has(node.key || '');
      if (node.children) {
        this.applyExpansion(node.children);
      }
    }
  }

  private collectSubtreePaths(node: TreeNode): string[] {
    const paths = [ node.key || '' ];
    for (const child of node.children || []) {
      paths.push(...this.collectSubtreePaths(child));
    }
    return paths;
  }

  private lastIndexInTier(items: string[], path: string): number {
    let last = -1;
    items.forEach((item, index) => {
      if (isInSourceTier(item, path)) { last = index; }
    });
    return last;
  }
}
